import { type CSSProperties, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'

// Toast — a brief, self-dismissing confirmation of an action's result
// (design/components/toast.html). Stacks bottom-right, max 3 visible. ok/info/
// warn auto-dismiss (with a countdown bar that PAUSES on hover/focus); errors
// PERSIST until closed (never lose an error). Imperative API: toast.ok(...),
// toast.error(..., { action }). Mount <Toaster/> once at the app root.

export type ToastTone = 'ok' | 'info' | 'warn' | 'sens'

export interface ToastOptions {
  description?: string
  action?: { label: string; onClick: () => void }
  /** ms; ignored for errors (they don't auto-dismiss). */
  duration?: number
}

interface ToastRecord extends ToastOptions {
  id: string
  tone: ToastTone
  title: string
}

// ── tiny external store (so the imperative API works without context) ──
let records: ToastRecord[] = []
let counter = 0
const listeners = new Set<() => void>()

function emit() {
  listeners.forEach((l) => l())
}
function subscribe(l: () => void) {
  listeners.add(l)
  return () => listeners.delete(l)
}
function getSnapshot() {
  return records
}
function push(tone: ToastTone, title: string, opts: ToastOptions = {}) {
  const id = `toast-${++counter}`
  records = [{ id, tone, title, ...opts }, ...records]
  emit()
  return id
}
function dismiss(id: string) {
  records = records.filter((r) => r.id !== id)
  emit()
}

/** Imperative toast API — toast.ok/info/warn/error(title, opts) returns an id;
 *  toast.dismiss(id) removes it. `error` maps to tone 'sens' and never
 *  auto-dismisses. Requires <Toaster/> mounted once at the app root. */
export const toast = {
  ok: (title: string, opts?: ToastOptions) => push('ok', title, opts),
  info: (title: string, opts?: ToastOptions) => push('info', title, opts),
  warn: (title: string, opts?: ToastOptions) => push('warn', title, opts),
  error: (title: string, opts?: ToastOptions) => push('sens', title, opts),
  dismiss,
}

// ── visuals ──
const toneCfg: Record<ToastTone, { box: string; bar: string; Icon: () => React.ReactNode }> = {
  ok: { box: 'bg-ok-bg text-ok', bar: 'bg-ok', Icon: CheckIcon },
  info: { box: 'bg-info-bg text-info', bar: 'bg-brand-blue', Icon: InfoIcon },
  warn: { box: 'bg-warn-bg text-warn', bar: 'bg-warn', Icon: WarnIcon },
  sens: { box: 'bg-sens-bg text-sens', bar: 'bg-sens', Icon: ErrorIcon },
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}
function InfoIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9.5" />
      <path d="M12 11v5M12 7.5h.01" />
    </svg>
  )
}
function WarnIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M10.3 3.1 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.1a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  )
}
function ErrorIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9.5" />
      <path d="M12 8v5M12 16h.01" />
    </svg>
  )
}

function ToastItem({ record, defaultDuration }: { record: ToastRecord; defaultDuration: number }) {
  const { t } = useTranslation('components')
  const cfg = toneCfg[record.tone]
  const autoDismiss = record.tone !== 'sens' && record.duration !== 0
  const duration = record.duration ?? defaultDuration
  const [paused, setPaused] = useState(false)
  const remainingRef = useRef(duration)
  const startRef = useRef(0)

  useEffect(() => {
    if (!autoDismiss || paused) return
    startRef.current = Date.now()
    const timer = window.setTimeout(() => dismiss(record.id), remainingRef.current)
    return () => {
      window.clearTimeout(timer)
      remainingRef.current -= Date.now() - startRef.current
    }
  }, [autoDismiss, paused, record.id])

  const barStyle: CSSProperties = {
    animationDuration: `${duration}ms`,
    animationPlayState: paused ? 'paused' : 'running',
  }

  return (
    <div
      role={record.tone === 'sens' ? 'alert' : 'status'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className="pointer-events-auto relative flex w-[340px] max-w-[calc(100vw-2rem)] items-start gap-[11px] overflow-hidden rounded-[11px] border border-line bg-surface p-[11px_14px] shadow-card"
    >
      <span
        className={cn(
          'flex size-6 flex-none items-center justify-center rounded-sm [&_svg]:size-[14px]',
          cfg.box,
        )}
      >
        <cfg.Icon />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-semibold leading-snug text-text">{record.title}</div>
        {record.description && (
          <div className="mt-[3px] text-[11.5px] leading-snug text-text-2">
            {record.description}
          </div>
        )}
        {record.action && (
          <div className="mt-2 flex gap-3">
            <button
              type="button"
              onClick={() => {
                record.action?.onClick()
                dismiss(record.id)
              }}
              className="text-[11.5px] font-semibold text-link hover:underline"
            >
              {record.action.label}
            </button>
          </div>
        )}
      </div>
      <button
        type="button"
        aria-label={t('ui.close')}
        onClick={() => dismiss(record.id)}
        className="flex size-[22px] flex-none items-center justify-center rounded-md text-text-3 transition-colors hover:bg-hover hover:text-text [&_svg]:size-3"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          aria-hidden
        >
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>
      {autoDismiss && (
        <span
          aria-hidden
          style={barStyle}
          className={cn(
            'absolute bottom-0 left-0 h-0.5 animate-toast-progress motion-reduce:hidden',
            cfg.bar,
          )}
        />
      )}
    </div>
  )
}

export interface ToasterProps {
  /** Max simultaneously visible (default 3). */
  max?: number
  /** Default auto-dismiss ms for non-error toasts (default 5000). */
  defaultDuration?: number
}

/** Portal rendering the toast stack bottom-right, newest first, `max` visible.
 *  The auto-dismiss countdown pauses on hover/focus. Mount exactly once at the
 *  app root; the `toast` API feeds it through an external store. */
export function Toaster({ max = 3, defaultDuration = 5000 }: ToasterProps) {
  const list = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  if (typeof document === 'undefined') return null
  const visible = list.slice(0, max)
  return createPortal(
    <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex flex-col items-end gap-2.5">
      {visible.map((r) => (
        <ToastItem key={r.id} record={r} defaultDuration={defaultDuration} />
      ))}
    </div>,
    document.body,
  )
}
